import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useServiceDebt } from "../../../hooks/useServiceDebt";
import ServiceDebtOverlay from "../../../components/shared/service-debt-overlay/ServiceDebtOverlay";
import { PackagesService } from "../../../services/packages.service";
import {
  ApiPaginationResponse,
  PackagesDataType,
} from "../../../interface/site.interface";
import Spinner from "../../../components/shared/spinner/Spinner";
import PackageItem from "../../../components/pages/admin-panels/my-packages/packages-item/PackageItem";
import StatusFilter from "../../../components/pages/admin-panels/my-packages/status-filter/StatusFilter";
import TitleUpdater from "../../../components/shared/title/TitleUpdater";
import styles from "./MyPackages.module.scss";
import checked from "../../../assets/images/pages/login/checkBox.svg";
import slider from "../../../assets/images/user-admin-panel/my-packages/Sliders.svg";
import MainButton from "../../../components/shared/button/MainButton";
import MoreInfoModal from "./more-info-modal/MoreInfoModal";
import PaymentModal from "../../../components/pages/admin-panels/my-packages/payment-modal/PaymentModal";
import WarningModal from "../../../components/pages/admin-panels/my-packages/warning-modal/WarningModal";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import { fetchTranslation } from "../../../redux/Slices/translationDataSlice";
import ArrowImage from "../../../assets/images/pages/login/CaretLeft.svg";
import { toast } from "react-toastify";
import { $api } from "../../../api/api";
import { Select } from "antd";
import SelectComponent from "../../../components/shared/select/SelectComponent";

const MyPackages = () => {
  const { serviceDebt, orderDebt } = useServiceDebt();
  const language = useSelector((state: RootState) => state.language.language);
  const [packages, setPackages] = useState<PackagesDataType[]>([]);
  const [checkedCards, setCheckedCards] = useState<number[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [filters, setFilters] = useState<boolean>(false);
  const [allPayCheckBox, setAllPayCheckBox] = useState(false);
  const [paginationData, setPaginationData] =
    useState<ApiPaginationResponse<PackagesDataType> | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<{
    key: string;
    label: string;
  }>(() => {
    const savedStatusKey =
      localStorage.getItem("selectedStatusKey") || "warehouse";
    return { key: savedStatusKey, label: "" };
  });

  const { data } = useSelector((state: RootState) => state.translationData);
  const dispatch = useDispatch<AppDispatch>();

  const [isPreDeclaration, setIsPreDeclaration] = useState(false);
  const [packagesType, setPackagesType] = useState<any[]>([]);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [editInvoys, setEditInvoys] = useState(false);
  const [warningModalOpen, setWarningModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({
    content: "",
    amount_paid: "",
    balance: "",
    currency: "",
  });

  const filterRef = useRef<HTMLDivElement>(null);
  const initialRender = useRef(true);

  // useCallback ilə stabil funksiyalar
  const fetchPackages = useCallback(async () => {
    try {
      setLoading(true);
      const response = await PackagesService.getAllPackages(selectedStatus.key);
      if (response?.data?.original?.packages?.data) {
        setPackages(response.data.original.packages.data);
        setPaginationData(response.data.original.packages);
        setIsPreDeclaration(response.data.original.type === "pre_declaration");
      } else {
        setPackages([]);
        setIsPreDeclaration(false);
      }
    } catch (error) {
      setPackages([]);
    } finally {
      setLoading(false);
    }
  }, [selectedStatus.key]);

  const fetchPackagesType = useCallback(async () => {
    try {
      const res = await PackagesService.getAllPackagesType();
      const statusOptions = res.data.data.map(
        (type: { key: string; label: string }) => ({
          key: type.key,
          label: type.label,
        })
      );

      setPackagesType(statusOptions);

      const matchedStatus = statusOptions.find(
        (option: any) => option.key === selectedStatus.key
      );

      if (matchedStatus) {
        setSelectedStatus((prev) => ({
          key: prev.key, // Key saxlanılır
          label: matchedStatus.label, // Yalnız label yenilənir
        }));
      }
    } catch (error) {
      console.error("Package types fetch error:", error);
    }
  }, []);

  const handleStatusChange = useCallback((newStatus: any) => {
    setSelectedStatus(newStatus);
    localStorage.setItem("selectedStatusKey", newStatus.key);
  }, []);

  // useEffect hookları
  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      if (!data) {
        dispatch(fetchTranslation());
      }
      fetchPackagesType();
    } else {
      dispatch(fetchTranslation());
    }
  }, [language, dispatch, data, fetchPackagesType]);

  useEffect(() => {
    const savedStatusKey = localStorage.getItem("selectedStatusKey");
    if (savedStatusKey && savedStatusKey !== selectedStatus.key) {
      const matchedStatus = packagesType.find(
        (opt) => opt.key === savedStatusKey
      );
      if (matchedStatus) {
        setSelectedStatus(matchedStatus);
      }
    }
  }, [packagesType, selectedStatus.key]);

  const finalAmount = useMemo(() => {
    const selectedPackages = packages.filter((pack) =>
      checkedCards.includes(pack.id)
    );
    const total = selectedPackages.reduce((sum, pack) => {
      // console.log(pack);
      const finalAmount = pack.discounted_amount_usd ?? pack.amount_usd ?? 0;
      return sum + parseFloat(finalAmount.toString());
    }, 0);

    return total.toFixed(2);
  }, [checkedCards, packages]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterRef.current &&
        !filterRef.current.contains(event.target as Node)
      ) {
        setFilters(false);
      }
    };

    if (filters) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [filters]);

  const unpaidPackages = packages.filter((pkg) => pkg.paid_status === 0);

  const handlePageChange = async (urlOrPage: string | number) => {
    setLoading(true);
    try {
      let apiUrl;
      if (typeof urlOrPage === "string") {
        apiUrl = urlOrPage;
      } else {
        apiUrl = $api("get_packages", {
          status: selectedStatus.key,
          page: urlOrPage,
        });
      }

      const response = await PackagesService.getAllPackages(
        selectedStatus.key,
        apiUrl
      );
      if (response.data?.original) {
        setPackages(response?.data?.original?.packages?.data || []);
        setPaginationData(response?.data?.original?.packages);
      }
    } catch (error) {
      toast.error(data?.error_occurred);
      console.error("Pagination error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id={styles.my_packages}>
      {(serviceDebt.hasDebt || orderDebt.hasDebt) && <ServiceDebtOverlay serviceDebt={serviceDebt} orderDebt={orderDebt} />}
      <TitleUpdater
        title={
          data?.colibri && data?.auth_my_parcels
            ? `${data?.colibri} | ${data?.auth_my_parcels}`
            : "Colibri"
        }
      />
      {loading ? (
        <Spinner size="large" />
      ) : (
        <div>
          <div>
            <div className={styles.pagination_part}>
              {(paginationData?.current_page === 1 &&
                paginationData?.next_page_url === null) ||
                (paginationData && (
                  <div className={styles.pagination}>
                    <img
                      src={ArrowImage}
                      className={`${styles.pagination_arrow_left} ${
                        !paginationData.prev_page_url ? styles.disabled : ""
                      }`}
                      alt="Prev"
                      onClick={() => {
                        if (paginationData.prev_page_url) {
                          handlePageChange(paginationData.prev_page_url);
                        }
                      }}
                    />

                    {[...Array(paginationData.last_page)].map((_, index) => {
                      const page = index + 1;
                      return (
                        <p
                          key={page}
                          className={`${styles.pagination_count} ${
                            paginationData.current_page === page
                              ? styles.active
                              : ""
                          }`}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </p>
                      );
                    })}

                    <img
                      src={ArrowImage}
                      className={`${styles.pagination_arrow_right} ${
                        !paginationData.next_page_url ? styles.disabled : ""
                      }`}
                      alt="Next"
                      onClick={() => {
                        if (paginationData.next_page_url) {
                          handlePageChange(paginationData.next_page_url);
                        }
                      }}
                    />
                  </div>
                ))}
            </div>
            <div className={styles.my_packages}>
              <div className={styles.headline}>
                <div className={styles.headline_end}>
                  {!isPreDeclaration && packages?.length > 0 ? (
                    <button
                      className={styles.pay_all}
                      onClick={() => {
                        setAllPayCheckBox(!allPayCheckBox);
                        if (!allPayCheckBox) {
                          const allIds = packages
                            .filter((pack) => pack.paid_status === 0)
                            .map((pack) => pack.id);
                          setCheckedCards(allIds);
                        } else {
                          setCheckedCards([]);
                        }
                      }}
                      disabled={unpaidPackages.length === 0}
                    >
                      <div className={styles.circle}>
                        {allPayCheckBox && (
                          <img
                            className={styles.checkedbox_img}
                            src={checked}
                            alt="Checked"
                          />
                        )}
                      </div>
                      <p>{data?.bulk_payment}</p>
                    </button>
                  ) : (
                    <p></p>
                  )}
                  {/* <div
                    onClick={() => setFilters(!filters)}
                    className={styles.icon}
                  >
                    <img src={slider} alt="Filters" />
                  </div> */}
                  <div className={styles.selectType}>
                    <SelectComponent
                      name="status"
                      customStyles={{ borderColor: "black" }}
                      customTextStyles={{ color: "black", fontWeight: "600" }}
                      selectedValue={selectedStatus.key}
                      value={selectedStatus.key}
                      options={packagesType.map((type) => ({
                        label: type.label,
                        value: type.key,
                      }))}
                      placeholder={data?.auth_courier_select || "Status seçin"}
                      onChange={(optionValue) => {
                        const selected = packagesType.find(
                          (opt) => opt.key === optionValue
                        );
                        if (selected) {
                          setSelectedStatus(selected);
                          localStorage.setItem(
                            "selectedStatusKey",
                            selected.key
                          );
                          setAllPayCheckBox(false);
                          setCheckedCards([]);
                          if (selected.key === "warehouse") {
                            setEditInvoys(true);
                          } else {
                            setEditInvoys(false);
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className={styles.my_packages_cards}>
                {packages.length > 0 ? (
                  packages.map((pack, idx) => {
                    const isChecked = checkedCards.includes(pack.id);
                    return (
                      <PackageItem
                        key={pack.id}
                        pack={pack}
                        isChecked={isChecked}
                        idx={idx}
                        fetchPackages={fetchPackages}
                        fetchPackagesType={fetchPackagesType}
                        setCheckedCards={setCheckedCards}
                        showEdit={editInvoys}
                        setPaymentModalOpen={setPaymentModalOpen}
                        isPreDeclaration={isPreDeclaration}
                      />
                    );
                  })
                ) : (
                  <div className={styles.empty_wrapper}>
                    <div className={styles.empy_packages}>
                      {data?.auth_no_data_in_section}
                    </div>
                  </div>
                )}
              </div>
              {!isPreDeclaration && packages.length > 0 && (
                <div className={styles.paymant_all_section}>
                  <div className={styles.paymant_detail}>
                    <div className={styles.final_payment}>
                      <p>{data?.total_amount}</p>
                      <p className={styles.final_price}>{finalAmount} USD</p>
                    </div>
                    <MainButton
                      text={data?.auth_pay}
                      onClick={() => setPaymentModalOpen(true)}
                      disabled={!checkedCards?.length}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* <MoreInfoModal /> */}
      {/* {filters && (
        <div ref={filterRef}>
          <StatusFilter
            selectedStatus={selectedStatus}
            setSelectedStatus={handleStatusChange}
            packagesType={packagesType}
            setEditInvoys={setEditInvoys}
            setFilters={setFilters}
            setAllPayCheckBox={setAllPayCheckBox}
            setCheckedCards={setCheckedCards}
          />
        </div>
      )} */}

      <PaymentModal
        paymentModalOpen={paymentModalOpen}
        setPaymentModalOpen={setPaymentModalOpen}
        checkedCards={checkedCards}
        fetchPackagesType={fetchPackagesType}
        setWarningModalOpen={setWarningModalOpen}
        setModalContent={setModalContent}
        fetchPackages={fetchPackages}
        totalAmount={finalAmount}
        onPaymentSuccess={() => {
          setCheckedCards([]);
          setAllPayCheckBox(false);
        }}
      />
      {warningModalOpen && (
        <WarningModal
          warningModalOpen={warningModalOpen}
          setWarningModalOpen={setWarningModalOpen}
          content={modalContent}
        />
      )}
    </div>
  );
};

export default MyPackages;
